import { ref, set, onValue } from 'firebase/database';
import { doc, updateDoc } from 'firebase/firestore';
import { database, firestore } from '../../../firebaseConfig';
import { CLOUD_FUNCTION_URLS } from '../../../utils/cloudFunctions';

// E-Mail für einen bestimmten Kontakt generieren
export const generateEmailForContact = async (
  contact, 
  selectedTopic, 
  selectedStyle, 
  contextInfo, 
  userId, 
  userSettings, 
  aiSettings, 
  userTopics, 
  topicTypeMapping,
  getDefaultTopicNote
) => {
  try {
    // Erstelle einen kontaktspezifischen Prompt
    const promptForContact = generatePromptForContact(
      contact, 
      selectedTopic, 
      selectedStyle, 
      contextInfo, 
      userSettings, 
      aiSettings, 
      userTopics, 
      topicTypeMapping,
      getDefaultTopicNote
    );
    
    // Rufe die KI-API mit dem generierten Prompt auf
    const apiResponse = await callAiApi(
      promptForContact, 
      contact, 
      selectedTopic,
      selectedStyle,
      userId, 
      userSettings, 
      aiSettings, 
      topicTypeMapping,
      userTopics
    );
    
    return {
      subject: apiResponse.subject || `Zusammenarbeit mit ${contact.business_name || 'Ihrem Unternehmen'}`,
      body: apiResponse.body || '',
      prompt: promptForContact
    };
  } catch (error) {
    console.error(`Fehler bei der E-Mail-Generierung für Kontakt:`, error);
    // Fallback für Fehlerszenarien
    return {
      subject: `Zusammenarbeit mit ${contact.business_name || 'Ihrem Unternehmen'}`,
      body: `Hallo ${contact.lead_firstname || 'dort'},\n\nVielen Dank für dein Interesse an BonusClub. Wir freuen uns darauf, mit dir zusammenzuarbeiten.\n\nMit freundlichen Grüßen,\n${userSettings.sender_name || userSettings.signature_name || userSettings.user_name || 'Das BonusClub-Team'}`,
      error: true,
      errorMessage: error.message
    };
  }
};

// Prompt für einen Kontakt generieren
const generatePromptForContact = (
  contact, 
  selectedTopic, 
  selectedStyle, 
  contextInfo, 
  userSettings, 
  aiSettings,
  userTopics,
  topicTypeMapping,
  getDefaultTopicNote
) => {
  let topicSpecificNote = "";
  
  console.log('📧 [BULK-PROMPT] Generating prompt for contact:', contact.business_name);
  console.log('📧 [BULK-PROMPT] selectedTopic:', selectedTopic);
  console.log('📧 [BULK-PROMPT] contact has tags:', !!contact.tags);
  
  // Prüfe, ob das selectedTopic von einem AI Tag stammt
  let isAiTagTopic = false;
  if (contact.tags && typeof contact.tags === 'object') {
    const tagsArray = Object.entries(contact.tags).map(([tagId, tagData]) => ({
      id: tagId,
      ...tagData
    }));
    
    // Sortiere nach appliedAt Datum (neueste zuerst)
    tagsArray.sort((a, b) => {
      const dateA = new Date(a.appliedAt || 0);
      const dateB = new Date(b.appliedAt || 0);
      return dateB - dateA;
    });
    
    if (tagsArray.length > 0) {
      const newestTag = tagsArray[0];
      
      // 🔥 WICHTIG: Immer die neueste Version aus userTopics laden (nicht vom Kontakt)
      // 🔥 LÖSUNG: IGNORIERE appliedTopic - verwende NUR AI Tag ID!
      if (newestTag.id) {
        const matchingTopic = Object.values(userTopics).find(topicData => {
          return topicData.aiTagId === newestTag.id;
        });
        
        if (matchingTopic && matchingTopic.topic === selectedTopic && matchingTopic.subjectLine) {
          // Platzhalter ersetzen
          let subject = matchingTopic.subjectLine;
          subject = subject.replace(/{{business_name}}/g, contact.business_name || 'Ihr Unternehmen');
          subject = subject.replace(/{{name}}/g, contact.lead_firstname || 'dort');
          subject = subject.replace(/{{company}}/g, 'BonusClub');
          console.log('📧 [BULK-PROMPT] ✅ Using AI Tag subject line from settings (by ID only):', subject);
          return subject;
        }
      }
      
      // Fallback: Prüfe alte gespeicherte Version
      if (!isAiTagTopic && newestTag.selectedTopic && newestTag.selectedTopic === selectedTopic) {
        isAiTagTopic = true;
        console.log('📧 [BULK-PROMPT] Topic comes from stored AI Tag (legacy), searching in userTopics...');
      }
    }
  }
  
  // Wenn das Topic von einem AI Tag stammt, suche direkt in userTopics
  if (isAiTagTopic) {
    // Suche nach dem Topic in userTopics (sowohl selectedTopics als auch customTopics)
    const matchingTopic = Object.values(userTopics).find(topicData => {
      // Prüfe sowohl topic als auch type Felder
      return topicData.topic === selectedTopic || topicData.type === selectedTopic;
    });
    
    if (matchingTopic) {
      console.log('📧 [BULK-PROMPT] Found matching topic in userTopics:', matchingTopic);
      
      if (matchingTopic.topicSpecificNote) {
        topicSpecificNote = matchingTopic.topicSpecificNote;
        console.log('📧 [BULK-PROMPT] ✅ Using topic note from userTopics:', topicSpecificNote);
      } else {
        console.log('📧 [BULK-PROMPT] ⚠️ No topicSpecificNote in matching topic');
      }
    } else {
      console.log('📧 [BULK-PROMPT] ⚠️ No matching topic found in userTopics for:', selectedTopic);
    }
  }
  
  // Fallback auf die normale Topic-Type Logik, wenn kein AI Tag Topic oder keine Note gefunden
  if (!topicSpecificNote) {
    console.log('📧 [BULK-PROMPT] Using standard topic type logic');
    
    const topicType = topicTypeMapping[selectedTopic];
    
    if (topicType) {
      // Für andere Typen: Suche nach dem Topic mit dem entsprechenden Typ
      const foundTopic = Object.values(userTopics).find(topic => topic.type === topicType);
      
      if (foundTopic && foundTopic.topicSpecificNote) {
        topicSpecificNote = foundTopic.topicSpecificNote;
        console.log('📧 [BULK-PROMPT] Using topic note from topic type:', topicSpecificNote);
      } else {
        // Fallback auf Standard-Anweisungen
        topicSpecificNote = getDefaultTopicNote(topicType);
        console.log('📧 [BULK-PROMPT] Using default topic note:', topicSpecificNote);
      }
    }
  }
  
  // Erstelle eine schlanke Zusammenfassung der Kontaktdaten für die KI
  const businessName = contact.business_name || 'Unbekanntes Unternehmen';
  const firstNameOrContact = contact.lead_firstname || 'Kontakt';
  const businessType = contact.lead_type || 'Unternehmer';
  const businessDescription = contact.lead_description || '';
  const businessCity = contact.lead_city || '';
  const website = contact.lead_website || '';
  
  // Erstelle den Prompt mit allen relevanten Informationen
  const prompt = `
    BUSINESS INFORMATION:
    Name: ${businessName}
    Type: ${businessType}
    Description: ${businessDescription}
    City: ${businessCity}
    ${website ? `Website: ${website}` : ''}
    
    CONTACT INFORMATION:
    Name: ${firstNameOrContact} ${contact.lead_lastname || ''}
    
    CONTEXT:
    ${contextInfo || ''}
    
    EMAIL TYPE:
    ${selectedTopic}
    
    SPECIFIC NOTE FOR THIS TYPE OF EMAIL:
    ${topicSpecificNote}
    
    CRITICAL INSTRUCTIONS:
    - Write in ${selectedStyle} tone
    - Keep it concise and to the point
    - Include a clear call to action
    - Sign off as ${userSettings.sender_name || userSettings.signature_name || userSettings.user_name || "Your Name"}
    - NEVER include a subject line in the email body
    - NEVER write "Subject:" followed by text in the email content
    - Start the email directly with the greeting (e.g., "Hallo [Name]," or "Dear [Name],")
    - The email body should ONLY contain the actual email message, no subject line text
    ${aiSettings.additional_instructions || ""}
    
    Generate ONLY the email body content. Do not include any subject line in your response.
  `;
  
  return prompt;
};

// API für E-Mail-Generierung aufrufen
const callAiApi = async (
  prompt, 
  contact, 
  selectedTopic,
  selectedStyle,
  userId,
  userSettings,
  aiSettings,
  topicTypeMapping,
  userTopics = {}
) => {
  // KI-Modellparameter
  const model = aiSettings.model || 'gpt-4o-mini';
  const temperature = parseFloat(aiSettings.temperature) || 0.9;
  const maxTokens = parseInt(aiSettings.max_tokens) || 250;
  const language = aiSettings.language || 'English';
  const promptTemplate = aiSettings.promptTemplate || null;
  const strictInstructions = aiSettings.strictInstructions || null;
  
  // Bestimme den Betreff basierend auf dem Topic und dem Kontakt
  console.log('📧 [BULK-API] Determining subject line for topic:', selectedTopic);
  console.log('📧 [BULK-API] Contact has tags:', !!contact.tags);
  
  // Hole eine kontaktspezifische Betreffzeile
  const subjectLine = await (async () => {
    // 🔥 NEUE LOGIK: Lade die AI Tag Settings direkt aus der Datenbank (wie in BulkModeEmail.js)
    if (contact.tags && typeof contact.tags === 'object') {
      const tagsArray = Object.entries(contact.tags).map(([tagId, tagData]) => ({
        id: tagId,
        ...tagData
      }));
      
      // Sortiere nach appliedAt Datum (neueste zuerst)
      tagsArray.sort((a, b) => {
        const dateA = new Date(a.appliedAt || 0);
        const dateB = new Date(b.appliedAt || 0);
        return dateB - dateA;
      });
      
      if (tagsArray.length > 0) {
        const newestTag = tagsArray[0];
        
        // 🔥 WICHTIG: Lade die AI Tag-Einstellungen direkt aus der Datenbank
        if (newestTag.id) {
          try {
            // Lade die AI Tag-Einstellungen
            const aiTagRef = ref(database, `/World/${userId}/settings/aiTags/${newestTag.id}`);
            const aiTagSnapshot = await new Promise((resolve) => {
              onValue(aiTagRef, resolve, { onlyOnce: true });
            });
            
            if (aiTagSnapshot.exists()) {
              const aiTagData = aiTagSnapshot.val();
              console.log('📧 [BULK-API] ✅ Loaded AI Tag settings from database:', {
                selectedTopic: aiTagData.selectedTopic,
                tag: aiTagData.tag,
                name: aiTagData.name
              });
              
              if (aiTagData.selectedTopic === selectedTopic) {
                // Finde das matching Topic in userTopics für die Subject Line
                const matchingTopic = Object.values(userTopics).find(topicData => {
                  return topicData.topic === aiTagData.selectedTopic || topicData.type === aiTagData.selectedTopic;
                });
                
                if (matchingTopic && matchingTopic.subjectLine) {
                  // Platzhalter ersetzen
                  let subject = matchingTopic.subjectLine;
                  subject = subject.replace(/{{business_name}}/g, contact.business_name || 'Ihr Unternehmen');
                  subject = subject.replace(/{{name}}/g, contact.lead_firstname || 'dort');
                  subject = subject.replace(/{{company}}/g, 'BonusClub');
                  console.log('📧 [BULK-API] ✅ Using AI Tag subject line from database settings (like Normal-Mode):', subject);
                  return subject;
                }
              }
            } else {
              console.log('📧 [BULK-API] ⚠️ AI Tag settings not found for ID:', newestTag.id);
            }
          } catch (error) {
            console.error('📧 [BULK-API] Error loading AI Tag settings:', error);
          }
        }
      }
    }
    
    console.log('📧 [BULK-API] Using standard logic for subject');
    
    // Fallback: Standard-Logik
    const topicType = topicTypeMapping[selectedTopic];
    if (topicType) {
      const matchingTopic = Object.values(userTopics).find(topic => topic && topic.type === topicType);
      if (matchingTopic && matchingTopic.subjectLine) {
        let subject = matchingTopic.subjectLine;
        subject = subject.replace(/{{business_name}}/g, contact.business_name || 'Ihr Unternehmen');
        subject = subject.replace(/{{name}}/g, contact.lead_firstname || 'dort');
        subject = subject.replace(/{{company}}/g, 'BonusClub');
        return subject;
      }
    }
    
    // Standard-Betreffzeilen als Fallback
    const defaultSubjects = {
      "Introduce Yourself": `BonusClub - Boost für ${contact.business_name || 'Ihr Unternehmen'}`,
      "Follow Up": `Nachfrage - BonusClub Partnerschaft mit ${contact.business_name || 'Ihrem Unternehmen'}`,
      "Second Follow Up": `Kurzes Update von BonusClub für ${contact.business_name || 'Ihr Unternehmen'}`,
      "Third Follow Up": `Finale Nachfrage von BonusClub für ${contact.business_name || 'Ihr Unternehmen'}`
    };
    
    return defaultSubjects[selectedTopic] || `BonusClub für ${contact.business_name || 'Ihr Unternehmen'}`;
  })();
  
  // Erstelle das Request-Objekt mit allen notwendigen Daten
  const requestData = {
    topic: selectedTopic,
    style: selectedStyle,
    context: prompt,
    businessName: contact.business_name || '',
    website: contact.lead_website || '',
    senderName: userSettings.sender_name || userSettings.user_name || '',
    senderEmail: userSettings.user_email || '',
    firstName: contact.lead_firstname || '',
    userId: userId,
    model: model,
    temperature: temperature,
    maxTokens: maxTokens,
    language: language,
    includeAllDetails: true,
    isReply: false,
    subjectLine: subjectLine,
    bulkMode: true,  // Spezielle Kennzeichnung für Bulk-Anfragen
    promptTemplate: promptTemplate,
    strictInstructions: strictInstructions,
    // 🔥 NEUE CRITICAL FLAGS um Subject im Body zu verhindern
    separateSubjectAndBody: true,
    excludeSubjectFromBody: true,
    bodyOnlyGeneration: true,
    preventSubjectInContent: true
  };
  
  try {
    // Verwende die API
    const response = await fetch(CLOUD_FUNCTION_URLS.worldEmailCreator(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData)
    });
    
    if (!response.ok) {
      throw new Error(`API-Fehler: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // Wenn die API erfolgreich war, gibt es eine emailContent-Eigenschaft
    if (data.emailContent) {
      return {
        subject: data.subjectLine || subjectLine,
        body: data.emailContent
      };
    } else {
      throw new Error('Unerwartetes API-Antwortformat');
    }
  } catch (error) {
    console.error('Fehler beim API-Aufruf:', error);
    
    // Fallback für API-Fehler
    return {
      subject: subjectLine || `Zusammenarbeit mit ${contact.business_name || 'Ihrem Unternehmen'}`,
      body: `Hallo ${contact.lead_firstname || 'dort'},\n\nVielen Dank für dein Interesse an BonusClub. Wir freuen uns darauf, mit dir zusammenzuarbeiten.\n\nMit freundlichen Grüßen,\n${userSettings.signature_name || 'Das BonusClub-Team'}`
    };
  }
};

// Kontaktstatus aktualisieren
export const updateContactStatus = async (userId, leadId, newStatus, onEmailResult) => {
  // Überprüfe, ob alle erforderlichen Parameter vorhanden sind
  if (!userId || !leadId || !newStatus) {
    console.warn("updateContactStatus: Fehlende Parameter", { userId, leadId, newStatus });
    return false;
  }
  
  try {
    // Erstelle einen Timestamp für konsistente Verwendung
    const timestamp = Date.now();
    
    // Debug-Nachricht für Statusänderung
    console.log(`Führe optimistisches Update für Lead ${leadId} auf "${newStatus}" durch`);
    
    // Optimistisches UI-Update
    if (onEmailResult) {
      // Erstelle das Update-Objekt mit allen notwendigen Informationen
      const statusUpdateInfo = {
        id: leadId,
        field: 'lead_contacted',
        value: newStatus,
        timestamp: timestamp,
        _optimistic_update: true,
        _prevent_refresh: true,
        _critical_update: true
      };
      
      // Sende das Status-Update zur übergeordneten Komponente
      onEmailResult('status-update', statusUpdateInfo);
      
      // Zusätzlich für die lokale UI aktualisieren
      const localUpdate = {
        id: leadId,
        field: 'lead_status',
        value: newStatus,
        timestamp: timestamp,
        _optimistic_update: true
      };
      onEmailResult('status-update', localUpdate);
    }
    
    // Bereite die Aktualisierungsdaten für Firebase vor
    const updateData = {
      lead_contacted: newStatus,
      lead_status: newStatus,
      last_contact_date: new Date().toISOString(),
      updated_at: timestamp,
      _optimistic_update: true,
      _client_update_timestamp: timestamp
    };
    
    // Aktualisiere die E-Mail-Zeitstempel, wenn eine E-Mail gesendet wurde
    if (newStatus !== "Not Contacted") {
      updateData.lead_last_email = {
        timestamp: timestamp,
        status: newStatus
      };
    }
    
    // Datenbank aktualisieren – jetzt Firestore statt Realtime DB
    const contactDocRef = doc(firestore, `World/${userId}/db/${leadId}`);
    await updateDoc(contactDocRef, updateData);
    
    console.log(`Status für Lead ${leadId} erfolgreich aktualisiert auf "${newStatus}"`);
    return true;
  } catch (error) {
    console.error("Fehler beim Aktualisieren des Kontaktstatus:", error);
    return false;
  }
};

// Letzten Absender in der Datenbank speichern
export const saveLastSenderToDatabase = async (senderEmail, userId) => {
  try {
    if (!userId || !senderEmail) return false;
    
    // Speichere den letzten verwendeten Absender in den Benutzereinstellungen
    const lastSenderRef = ref(database, `/World/${userId}/settings/lastUsedSender`);
    await set(lastSenderRef, {
      email: senderEmail,
      timestamp: Date.now(),
      updatedAt: new Date().toISOString()
    });
    
    console.log(`Letzter verwendeter Absender (${senderEmail}) in der Datenbank gespeichert`);
    return true;
  } catch (error) {
    console.error('Fehler beim Speichern des letzten Absenders:', error);
    return false;
  }
}; 